import React, { useState, useEffect } from 'react';
import { sharedStyles, getStatusColor, formatDate, formatCurrency } from '../styles/sharedStyles';
import { useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';
import { hideFinancialUi } from '../utils/foremanUi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Customers = () => {
  const api = useApi();
  const { user } = useUser();
  const canManage = user?.role === 'admin';
  const hideMoney = hideFinancialUi(user?.role);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('Active');
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [customerTickets, setCustomerTickets] = useState([]);
  const [customerJobs, setCustomerJobs] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loadingTicketDetails, setLoadingTicketDetails] = useState(false);

  const [formData, setFormData] = useState({
    companyName: 'N/A'+Math.random().toString(36).substring(2, 15),
    contactPerson: 'N/A',
    email: 'dummy@gmail.com',
    phone: 'N/A',
    address: 'N/A',
    city: 'N/A',
    state: 'N/A',
    zipCode: 'N/A',
    status: 'Active',
    notes: 'N/A',
    website: 'https://griffinland.itmsc.net/',
    taxId: 'N/A',
    paymentTerms: 'Net 30',
    creditLimit: '0',
    lastContactDate: new Date().toISOString().split('T')[0],
    nextFollowUpDate: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]
  });

  // Debounce search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Effect to debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to page 1 when search term changes
      setCurrentPage(1);
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch customers with pagination and filters
  const fetchCustomers = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(selectedStatus && { status: selectedStatus })
      });

      const response = await api({
        method: 'get',
        url: `/customers?${params}`
      });
      
      if (response.data.success) {
        setCustomers(response.data.data.customers);
        setTotalPages(response.data.data.pagination.totalPages);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
      setError(err.response?.data?.message || 'Failed to fetch customers');
    } finally {
      setLoading(false);
    }
  }, [api, currentPage, debouncedSearchTerm, selectedStatus]);

  // Reset to page 1 when status changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedStatus]);

  // Fetch customers when any dependency changes
  useEffect(() => {
    console.log(process.env.REACT_APP_API_BASE_URL);
    fetchCustomers();
  }, [fetchCustomers]);

  // Add effect to handle body scroll when modal is open
  useEffect(() => {
    if (showModal || showEditModal || showTicketModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showModal, showEditModal, showTicketModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const response = await api({
        method: 'post',
        url: `/customers`,
        data: formData
      });
      
      if (response.data.success) {
        await fetchCustomers();
        handleCloseModal();
      }
    } catch (err) {
      console.error('Error creating customer:', err);
      setError(err.response?.data?.message || 'Failed to create customer');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      companyName: customer.companyName || 'N/A',
      contactPerson: customer.contactPerson || 'N/A',
      email: customer.email || 'dummy@gmail.com',
      phone: customer.phone || 'N/A',
      address: customer.address || 'N/A',
      city: customer.city || 'N/A',
      state: customer.state || 'N/A',
      zipCode: customer.zipCode || 'N/A',
      status: customer.status || 'Active',
      notes: customer.notes || 'N/A',
      website: customer.website || 'https://griffinland.itmsc.net/',
      taxId: customer.taxId || 'N/A',
      paymentTerms: customer.paymentTerms || 'Net 30',
      creditLimit: customer.creditLimit || '0',
      lastContactDate: customer.lastContactDate ? customer.lastContactDate.split('T')[0] : new Date().toISOString().split('T')[0],
      nextFollowUpDate: customer.nextFollowUpDate ? customer.nextFollowUpDate.split('T')[0] : new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]
    });
    setShowEditModal(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const response = await api({
        method: 'put',
        url: `/customers/${editingCustomer.id}`,
        data: formData
      });
      
      if (response.data.success) {
        await fetchCustomers();
        handleCloseEditModal();
      }
    } catch (err) {
      console.error('Error updating customer:', err);
      setError(err.response?.data?.message || 'Failed to update customer');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        setLoading(true);
        const response = await api({
          method: 'delete',
          url: `/customers/${id}`
        });
        
        if (response.data.success) {
          fetchCustomers();
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete customer');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      companyName: 'N/A'+Math.random().toString(36).substring(2, 15),
      contactPerson: 'N/A',
      email: 'dummy@gmail.com',
      phone: 'N/A',
      address: 'N/A',
      city: 'N/A',
      state: 'N/A',
      zipCode: 'N/A',
      status: 'Active',
      notes: 'N/A',
      website: 'https://griffinland.itmsc.net/',
      taxId: 'N/A',
      paymentTerms: 'Net 30',
      creditLimit: '0',
      lastContactDate: new Date().toISOString().split('T')[0],
      nextFollowUpDate: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]
    });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingCustomer(null);
    setFormData({
      companyName: 'N/A'+Math.random().toString(36).substring(2, 15),
      contactPerson: 'N/A',
      email: 'N/A',
      phone: 'N/A',
      address: 'N/A',
      city: 'N/A',
      state: 'N/A',
      zipCode: 'N/A',
      status: 'Active',
      notes: 'N/A',
      website: 'N/A',
      taxId: 'N/A',
      paymentTerms: 'Net 30',
      creditLimit: '0',
      lastContactDate: new Date().toISOString().split('T')[0],
      nextFollowUpDate: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]
    });
  };

  const handleCustomerClick = async (customer) => {
    if (expandedCustomer?.id === customer.id) {
      setExpandedCustomer(null);
      setCustomerTickets([]);
      setCustomerJobs([]);
      return;
    }

    setExpandedCustomer(customer);
    setLoadingDetails(true);
    setDetailsError(null);

    try {
      const [ticketsResponse, jobsResponse] = await Promise.all([
        api({
          method: 'get',
          url: `/customers/${customer.id}/tickets`
        }),
        api({
          method: 'get',
          url: `/customers/${customer.id}/jobs`
        })
      ]);

      if (ticketsResponse.data.success) {
        setCustomerTickets(ticketsResponse.data.data);
      }
      if (jobsResponse.data.success) {
        setCustomerJobs(jobsResponse.data.data);
      }
    } catch (err) {
      console.error('Error fetching customer details:', err);
      setDetailsError('Failed to load customer details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketModal(true);
    setLoadingTicketDetails(true);

    try {
      // Try to fetch detailed ticket information
      const response = await api({
        method: 'get',
        url: `/tickets/${ticket.id}`
      });

      if (response.data.success) {
        setSelectedTicket(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      console.error('Error response:', err.response?.data);
      
      // If detailed fetch fails, fetch related data using the IDs we have
      try {
        const [customerResponse, jobResponse, employeeResponse] = await Promise.all([
          api({ method: 'get', url: `/customers/${ticket.customerId}` }),
          api({ method: 'get', url: `/jobs/${ticket.jobId}` }),
          api({ method: 'get', url: `/employees/${ticket.employeeId}` })
        ]);

        // Try to get ticket items from the detailed ticket endpoint
        let ticketItems = [];
        try {
          const ticketItemsResponse = await api({ method: 'get', url: `/tickets/${ticket.id}` });
          if (ticketItemsResponse.data.success && ticketItemsResponse.data.data.TicketItems) {
            ticketItems = ticketItemsResponse.data.data.TicketItems;
          }
        } catch (itemsErr) {
          console.log('Could not fetch ticket items, using empty array');
        }

        const enhancedTicket = {
          ...ticket,
          Customer: customerResponse.data.success ? customerResponse.data.data : null,
          Job: jobResponse.data.success ? jobResponse.data.data : null,
          Employee: employeeResponse.data.success ? employeeResponse.data.data : null,
          TicketItems: ticketItems
        };

        setSelectedTicket(enhancedTicket);
      } catch (fetchErr) {
        console.error('Error fetching related data:', fetchErr);
        setSelectedTicket(ticket);
      }
    } finally {
      setLoadingTicketDetails(false);
    }
  };

  const handleCloseTicketModal = () => {
    setShowTicketModal(false);
    setSelectedTicket(null);
    setLoadingTicketDetails(false);
  };

  return (
    <div style={sharedStyles.pageContainer} className="container-fluid px-2 px-md-4 py-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
        <h2 style={sharedStyles.pageTitle}>Customer Management</h2>
        {canManage && (
          <button 
            className="btn"
            style={sharedStyles.button.primary}
            onClick={() => setShowModal(true)}
          >
            <i className="bi bi-plus-circle me-2"></i>
            Add Customer
          </button>
        )}
      </div>

      {/* Search and Filter Section */}
      <div className="card mb-4" style={sharedStyles.card}>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <input
                type="text"
                className="form-control"
                placeholder="Search by company name, contact person, or email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <select
                className="form-select"
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="Pending">Pending</option>
                <option value="Blocked">Blocked</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center my-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Customers Table */}
      <div className="card" style={sharedStyles.card}>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table align-middle mb-0" style={sharedStyles.table}>
              <thead>
                <tr>
                  <th>Company Name</th>
                  <th>Contact Person</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Location</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers
                  .slice() // copy to avoid mutating state
                  .sort((a, b) => a.companyName.localeCompare(b.companyName, undefined, { sensitivity: 'base' }))
                  .map(customer => {
                  const statusStyle = getStatusColor(customer.status);
                  const location = [customer.city, customer.state].filter(Boolean).join(', ');
                  const isExpanded = expandedCustomer?.id === customer.id;
                  
                  return (
                    <React.Fragment key={customer.id}>
                      <tr 
                        onClick={() => handleCustomerClick(customer)}
                        style={{ cursor: 'pointer' }}
                        className={isExpanded ? 'table-active' : ''}
                      >
                        <td>
                          <div className="d-flex align-items-center">
                            {customer.companyName}
                            <i 
                              className={`bi bi-chevron-${isExpanded ? 'up' : 'down'} ms-2 text-primary`}
                              style={{ fontSize: '0.8rem' }}
                            ></i>
                          </div>
                        </td>
                        <td>{customer.contactPerson}</td>
                        <td>{customer.email || '-'}</td>
                        <td>{customer.phone || '-'}</td>
                        <td>{location || '-'}</td>
                        <td>
                          <span 
                            style={{
                              ...sharedStyles.statusBadge,
                              backgroundColor: statusStyle.bg,
                              color: statusStyle.text
                            }}
                          >
                            {customer.status}
                          </span>
                        </td>
                        <td>
                          <div className="btn-group">
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCustomerClick(customer);
                              }}
                              title="View Details"
                            >
                              <i className={`bi bi-${isExpanded ? 'chevron-up' : 'chevron-down'}`}></i>
                            </button>
                            {canManage && (
                              <>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(customer);
                                  }}
                                  title="Edit Customer"
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(customer.id);
                                  }}
                                  title="Delete Customer"
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan="7" className="p-0">
                            <div className="p-3 bg-light">
                              {loadingDetails ? (
                                <div className="text-center py-3">
                                  <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                </div>
                              ) : detailsError ? (
                                <div className="alert alert-danger mb-0">
                                  {detailsError}
                                </div>
                              ) : (
                                <div className="row g-4">
                                  {/* Recent Tickets */}
                                  <div className="col-md-6">
                                    <div className="card h-100 border-0 shadow-sm">
                                      <div className="card-header bg-white border-0">
                                        <h6 className="mb-0 d-flex align-items-center">
                                          <i className="bi bi-ticket-detailed me-2 text-primary"></i>
                                          Recent Tickets
                                        </h6>
                                      </div>
                                      <div className="card-body p-0">
                                        {customerTickets.length > 0 ? (
                                          <div className="list-group list-group-flush">
                                            {customerTickets.map(ticket => (
                                              <div 
                                                key={ticket.id} 
                                                className="list-group-item border-0 px-3 py-2"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleViewTicket(ticket)}
                                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                              >
                                                <div className="d-flex justify-content-between align-items-center">
                                                  <div className="flex-grow-1">
                                                    <div className="fw-medium d-flex align-items-center">
                                                      {ticket.ticketNumber}
                                                      <i className="bi bi-arrow-right ms-2 text-primary" style={{ fontSize: '0.8rem' }}></i>
                                                    </div>
                                                    <small className="text-muted">{formatDate(ticket.createdAt)}</small>
                                                    {!hideMoney && ticket.totalAmount && (
                                                      <div className="mt-1">
                                                        <small className="text-success fw-medium">
                                                          ${parseFloat(ticket.totalAmount).toFixed(2)}
                                                        </small>
                                                      </div>
                                                    )}
                                                  </div>
                                                  <span 
                                                    className={`badge ${
                                                      ticket.status === 'Pending' ? 'bg-warning' :
                                                      ticket.status === 'Approved' ? 'bg-success' :
                                                      ticket.status === 'Rejected' ? 'bg-danger' :
                                                      ticket.status === 'Paid' ? 'bg-primary' :
                                                      'bg-secondary'
                                                    }`}
                                                    style={{ fontSize: '0.8rem' }}
                                                  >
                                                    {ticket.status}
                                                  </span>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="text-center py-4 text-muted">
                                            <i className="bi bi-ticket-detailed mb-2" style={{ fontSize: '1.5rem' }}></i>
                                            <p className="mb-0">No recent tickets</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Recent Jobs */}
                                  <div className="col-md-6">
                                    <div className="card h-100 border-0 shadow-sm">
                                      <div className="card-header bg-white border-0">
                                        <h6 className="mb-0 d-flex align-items-center">
                                          <i className="bi bi-briefcase me-2 text-primary"></i>
                                          Recent Jobs
                                        </h6>
                                      </div>
                                      <div className="card-body p-0">
                                        {customerJobs.length > 0 ? (
                                          <div className="list-group list-group-flush">
                                            {customerJobs.map(job => (
                                              <div key={job.id} className="list-group-item border-0 px-3 py-2">
                                                <div className="d-flex justify-content-between align-items-center">
                                                  <div>
                                                    <div className="fw-medium">{job.title}</div>
                                                    <small className="text-muted">{formatDate(job.createdAt)}</small>
                                                  </div>
                                                  <span 
                                                    className={`badge bg-${getStatusColor(job.status).bg}`}
                                                    style={{ fontSize: '0.8rem' }}
                                                  >
                                                    {job.status}
                                                  </span>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="text-center py-4 text-muted">
                                            <i className="bi bi-briefcase mb-2" style={{ fontSize: '1.5rem' }}></i>
                                            <p className="mb-0">No recent jobs</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <nav className="mt-4" style={sharedStyles.pagination} aria-label="Customer table pagination">
            <ul className="pagination justify-content-center mb-0 flex-wrap">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button 
                  className="page-link" 
                  onClick={() => setCurrentPage(prev => prev - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
              </li>
              {[...Array(totalPages)].map((_, index) => (
                <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                  <button 
                    className="page-link"
                    onClick={() => setCurrentPage(index + 1)}
                  >
                    {index + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button 
                  className="page-link"
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Add Customer Modal */}
      {showModal && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={sharedStyles.modal.overlay}
            onClick={handleCloseModal}
          ></div>
          <div 
            className="modal fade show" 
            style={{ 
              display: 'block',
              zIndex: 1500
            }} 
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content" style={sharedStyles.modal.content}>
                <div className="modal-header border-0 pb-0">
                  <h5 style={sharedStyles.cardTitle}>Add New Customer</h5>
                  <button 
                    type="button" 
                    className="btn-close"
                    onClick={handleCloseModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleSubmit}>
                    {/* Required Fields */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Company Name</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Contact Person</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="contactPerson"
                          value={formData.contactPerson}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Email</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Phone</label>
                        <input
                          type="tel"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div className="row">
                      <div className="col-12 mb-3">
                        <label style={sharedStyles.formLabel}>Address</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-4 mb-3">
                        <label style={sharedStyles.formLabel}>City</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-4 mb-3">
                        <label style={sharedStyles.formLabel}>State</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-4 mb-3">
                        <label style={sharedStyles.formLabel}>ZIP Code</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Business Information */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Website</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Tax ID</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="taxId"
                          value={formData.taxId}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Financial Information */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Payment Terms</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="paymentTerms"
                          value={formData.paymentTerms}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Payment Terms</option>
                          <option value="Net 15">Net 15</option>
                          <option value="Net 30">Net 30</option>
                          <option value="Net 45">Net 45</option>
                          <option value="Net 60">Net 60</option>
                          <option value="Due on Receipt">Due on Receipt</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Credit Limit</label>
                        <input
                          type="number"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="creditLimit"
                          value={formData.creditLimit}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Status and Dates */}
                    <div className="row">
                      <div className="col-12 col-md-4 mb-3">
                        <label style={sharedStyles.formLabel}>Status</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                          <option value="Pending">Pending</option>
                          <option value="Blocked">Blocked</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-4 mb-3">
                        <label style={sharedStyles.formLabel}>Last Contact Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="lastContactDate"
                          value={formData.lastContactDate}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-4 mb-3">
                        <label style={sharedStyles.formLabel}>Next Follow-up Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="nextFollowUpDate"
                          value={formData.nextFollowUpDate}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="row">
                      <div className="col-12 mb-3">
                        <label style={sharedStyles.formLabel}>Notes</label>
                        <textarea
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          rows="3"
                        />
                      </div>
                    </div>

                    <div className="text-end mt-4">
                      <button 
                        type="button" 
                        className="btn me-2"
                        style={sharedStyles.button.secondary}
                        onClick={handleCloseModal}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn"
                        style={sharedStyles.button.primary}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Adding...
                          </>
                        ) : (
                          'Add Customer'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Customer Modal */}
      {showEditModal && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={sharedStyles.modal.overlay}
            onClick={handleCloseEditModal}
          ></div>
          <div 
            className="modal fade show" 
            style={{ 
              display: 'block',
              zIndex: 1500
            }} 
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content" style={sharedStyles.modal.content}>
                <div className="modal-header border-0 pb-0">
                  <h5 style={sharedStyles.cardTitle}>Edit Customer</h5>
                  <button 
                    type="button" 
                    className="btn-close"
                    onClick={handleCloseEditModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleUpdateSubmit}>
                    {/* Required Fields */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Company Name</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Contact Person</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="contactPerson"
                          value={formData.contactPerson}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Email</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Phone</label>
                        <input
                          type="tel"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div className="row">
                      <div className="col-12 mb-3">
                        <label style={sharedStyles.formLabel}>Address</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-12 col-md-4 mb-3">
                        <label style={sharedStyles.formLabel}>City</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-4 mb-3">
                        <label style={sharedStyles.formLabel}>State</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-4 mb-3">
                        <label style={sharedStyles.formLabel}>ZIP Code</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Business Information */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Website</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Tax ID</label>
                        <input
                          type="text"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="taxId"
                          value={formData.taxId}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Financial Information */}
                    <div className="row">
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Payment Terms</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="paymentTerms"
                          value={formData.paymentTerms}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Payment Terms</option>
                          <option value="Net 15">Net 15</option>
                          <option value="Net 30">Net 30</option>
                          <option value="Net 45">Net 45</option>
                          <option value="Net 60">Net 60</option>
                          <option value="Due on Receipt">Due on Receipt</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-6 mb-3">
                        <label style={sharedStyles.formLabel}>Credit Limit</label>
                        <input
                          type="number"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="creditLimit"
                          value={formData.creditLimit}
                          onChange={handleInputChange}
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {/* Status and Dates */}
                    <div className="row">
                      <div className="col-12 col-md-4 mb-3">
                        <label style={sharedStyles.formLabel}>Status</label>
                        <select
                          className="form-select"
                          style={sharedStyles.formSelect}
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                        >
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                          <option value="Pending">Pending</option>
                          <option value="Blocked">Blocked</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-4 mb-3">
                        <label style={sharedStyles.formLabel}>Last Contact Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="lastContactDate"
                          value={formData.lastContactDate}
                          onChange={handleInputChange}
                        />
                      </div>
                      <div className="col-12 col-md-4 mb-3">
                        <label style={sharedStyles.formLabel}>Next Follow-up Date</label>
                        <input
                          type="date"
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="nextFollowUpDate"
                          value={formData.nextFollowUpDate}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Notes */}
                    <div className="row">
                      <div className="col-12 mb-3">
                        <label style={sharedStyles.formLabel}>Notes</label>
                        <textarea
                          className="form-control"
                          style={sharedStyles.formControl}
                          name="notes"
                          value={formData.notes}
                          onChange={handleInputChange}
                          rows="3"
                        />
                      </div>
                    </div>

                    <div className="modal-footer border-0 pt-0">
                      <button 
                        type="button" 
                        className="btn me-2"
                        style={sharedStyles.button.secondary}
                        onClick={handleCloseEditModal}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button 
                        type="submit" 
                        className="btn"
                        style={sharedStyles.button.primary}
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Updating...
                          </>
                        ) : (
                          'Update Customer'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Ticket Details Modal */}
      {showTicketModal && selectedTicket && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={sharedStyles.modal.overlay}
            onClick={handleCloseTicketModal}
          ></div>
          <div 
            className="modal fade show" 
            style={{ 
              display: 'block',
              zIndex: 1500
            }} 
            tabIndex="-1"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content" style={sharedStyles.modal.content}>
                <div className="modal-header border-0 pb-0">
                  <h5 style={sharedStyles.cardTitle}>
                    <i className="bi bi-ticket-detailed me-2 text-primary"></i>
                    Ticket Details - {selectedTicket.ticketNumber}
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close"
                    onClick={handleCloseTicketModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  {loadingTicketDetails ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                                    ) : (
                    <div className="row g-3">
                        {/* Basic Information */}
                       <div className="col-12">
                         <div className="card border-0 bg-light">
                           <div className="card-body">
                             <div className="row g-3">
                               <div className="col-md-6">
                                 <label className="form-label fw-medium text-muted">Ticket Number</label>
                                 <div className="fw-bold">{selectedTicket.ticketNumber}</div>
                               </div>
                               <div className="col-md-6">
                                 <label className="form-label fw-medium text-muted">Status</label>
                                 <div>
                                   <span 
                                     className={`badge ${
                                       selectedTicket.status === 'Pending' ? 'bg-warning' :
                                       selectedTicket.status === 'Approved' ? 'bg-success' :
                                       selectedTicket.status === 'Rejected' ? 'bg-danger' :
                                       selectedTicket.status === 'Paid' ? 'bg-primary' :
                                       'bg-secondary'
                                     }`}
                                   >
                                     {selectedTicket.status}
                                   </span>
                                 </div>
                               </div>
                               <div className="col-md-6">
                                 <label className="form-label fw-medium text-muted">Customer</label>
                                 <div className="fw-medium">{selectedTicket.Customer?.companyName || 'N/A'}</div>
                               </div>
                               <div className="col-md-6">
                                 <label className="form-label fw-medium text-muted">Operator</label>
                                 <div className="fw-medium">{selectedTicket.Employee?.name || 'N/A'}</div>
                               </div>
                               <div className="col-md-6">
                                 <label className="form-label fw-medium text-muted">Job Name</label>
                                 <div className="fw-medium">{selectedTicket.Job?.title || 'N/A'}</div>
                               </div>
                               <div className="col-md-6">
                                 <label className="form-label fw-medium text-muted">Work Date</label>
                                 <div>{formatDate(selectedTicket.workDate)}</div>
                               </div>
                               <div className="col-md-6">
                                 <label className="form-label fw-medium text-muted">Issue Date</label>
                                 <div>{formatDate(selectedTicket.issueDate)}</div>
                               </div>
                             </div>
                           </div>
                         </div>
                       </div>

                      {/* Financial / work summary */}
                      <div className="col-12">
                        <div className="card border-0 bg-light">
                          <div className="card-body">
                            <h6 className="card-title mb-3">
                              <i className={`bi ${hideMoney ? 'bi-clock' : 'bi-currency-dollar'} me-2 ${hideMoney ? 'text-primary' : 'text-success'}`}></i>
                              {hideMoney ? 'Work summary' : 'Financial Details'}
                            </h6>
                            <div className="row g-3">
                              {!hideMoney && (
                                <div className="col-md-6">
                                  <label className="form-label fw-medium text-muted">Total Amount</label>
                                  <div className="fw-bold text-success">
                                    ${parseFloat(selectedTicket.totalAmount || 0).toFixed(2)}
                                  </div>
                                </div>
                              )}
                              <div className="col-md-6">
                                <label className="form-label fw-medium text-muted">Total Hours</label>
                                <div className="fw-bold">
                                  {parseFloat(selectedTicket.totalHours || 0).toFixed(2)} hrs
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Description and Notes */}
                      {(selectedTicket.description || selectedTicket.notes) && (
                        <div className="col-12">
                          <div className="card border-0 bg-light">
                            <div className="card-body">
                              {selectedTicket.description && (
                                <div className="mb-3">
                                  <label className="form-label fw-medium text-muted">Description</label>
                                  <div className="bg-white p-3 rounded border">
                                    {selectedTicket.description}
                                  </div>
                                </div>
                              )}
                              {selectedTicket.notes && (
                                <div>
                                  <label className="form-label fw-medium text-muted">Notes</label>
                                  <div className="bg-white p-3 rounded border">
                                    {selectedTicket.notes}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                                             {/* Ticket Items */}
                       {selectedTicket.TicketItems && selectedTicket.TicketItems.length > 0 && (
                         <div className="col-12">
                           <div className="card border-0 bg-light">
                             <div className="card-body">
                               <h6 className="card-title mb-3">
                                 <i className="bi bi-list-ul me-2 text-primary"></i>
                                 Ticket Items Details
                               </h6>
                               <div className="table-responsive">
                                 <table className="table table-sm table-bordered">
                                   <thead className="table-light">
                                     <tr>
                                       <th>Machine</th>
                                       <th>Operator</th>
                                       <th>Description</th>
                                       <th>Hours</th>
                                       {!hideMoney && (
                                         <>
                                           <th>Rate</th>
                                           <th>Amount</th>
                                         </>
                                       )}
                                     </tr>
                                   </thead>
                                   <tbody>
                                     {selectedTicket.TicketItems.map((item, index) => (
                                       <tr key={index}>
                                         <td>
                                           <div className="fw-medium">{item.Machine?.name || 'N/A'}</div>
                                           <small className="text-muted">{item.Machine?.model || ''}</small>
                                         </td>
                                         <td>{item.Employee?.name || 'N/A'}</td>
                                         <td>{item.description || 'N/A'}</td>
                                         <td className="text-center">{parseFloat(item.hoursUsed || 0).toFixed(2)}</td>
                                         {!hideMoney && (
                                           <>
                                             <td className="text-end">${parseFloat(item.hourlyRate || 0).toFixed(2)}</td>
                                             <td className="fw-bold text-end text-success">
                                               ${parseFloat(item.amount || 0).toFixed(2)}
                                             </td>
                                           </>
                                         )}
                                       </tr>
                                     ))}
                                   </tbody>
                                   <tfoot className="table-light">
                                     <tr>
                                       <td colSpan="3" className="fw-bold text-end">Total:</td>
                                       <td className="fw-bold text-center">
                                         {selectedTicket.TicketItems.reduce((sum, item) => sum + parseFloat(item.hoursUsed || 0), 0).toFixed(2)}
                                       </td>
                                       {!hideMoney && (
                                         <>
                                           <td></td>
                                           <td className="fw-bold text-end text-success">
                                             ${selectedTicket.TicketItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0).toFixed(2)}
                                           </td>
                                         </>
                                       )}
                                     </tr>
                                   </tfoot>
                                 </table>
                               </div>
                             </div>
                           </div>
                         </div>
                       )}
                      </div>
                  )}
                </div>
                <div className="modal-footer border-0 pt-0">
                  <button 
                    type="button" 
                    className="btn"
                    style={sharedStyles.button.secondary}
                    onClick={handleCloseTicketModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Customers; 